import React, { useState } from 'react'
import * as api from '../../../lib/api'
import { useToast } from '../../../contexts/ToastContext'
import { SkeletonRow } from '../../../components/Skeleton/Skeleton'
import CustomSelect from '../../../components/CustomSelect/CustomSelect'
import Modal from '../../../components/Modal'
import apiClient from '../../../lib/apiClient'
import DatePickerField from '../../../components/DatePickerField'

export default function TeamSettings({
  users,
  setUsers,
  loadingTeam,
  isOwner,
  isAdmin,
  profile,
  setProfile,
  user: currentUser,
  loadTeam,
  userPvzs
}) {
  const toast = useToast()

  // Внутреннее состояние
  const [showArchive, setShowArchive] = useState(false)
  const [mergeModal, setMergeModal] = useState(null)
  const [dismissModal, setDismissModal] = useState(null)
  const [pinResetModal, setPinResetModal] = useState(null)
  const [confirmRestore, setConfirmRestore] = useState(null)
  const [confirmHardDelete, setConfirmHardDelete] = useState(null)

  // Настройка доступа к ПВЗ
  const [pvzModalUser, setPvzModalUser] = useState(null)
  const [pvzList, setPvzList] = useState([])
  const [savingPvz, setSavingPvz] = useState(false)

  const handleOpenPvzModal = async (targetUser) => {
    setPvzModalUser(targetUser)
    try {
      const data = await apiClient(`/employees/${targetUser.id}/pvzs`)
      setPvzList(data)
    } catch (err) {
      toast.error('Не удалось загрузить список ПВЗ')
    }
  }

  const handleSavePvzs = async () => {
    if (!pvzModalUser) return
    setSavingPvz(true)
    try {
      const assignedPvzIds = pvzList.filter(p => p.assigned).map(p => p.id)
      const primaryPvz = pvzList.find(p => p.is_primary)
      const primaryPvzId = primaryPvz ? primaryPvz.id : (assignedPvzIds[0] || null)

      await apiClient(`/employees/${pvzModalUser.id}/pvzs`, {
        method: 'POST',
        body: JSON.stringify({
          pvzIds: assignedPvzIds,
          primaryPvzId
        })
      })

      toast.success('Привязка к ПВЗ успешно сохранена')
      setPvzModalUser(null)
      loadTeam(true)
    } catch (err) {
      toast.error('Не удалось сохранить привязку: ' + err.message)
    } finally {
      setSavingPvz(false)
    }
  }

  // Создание сотрудника
  const [isAddingEmployee, setIsAddingEmployee] = useState(false)
  const [newEmployee, setNewEmployee] = useState({ fullName: '', role: 'employee', hiredAt: new Date().toISOString().split('T')[0] })
  const [employeeSaving, setEmployeeSaving] = useState(false)

  const handleApproveUser = async (userId) => {
    const previousUsers = [...users];
    setUsers(users.map(u => u.id === userId ? { ...u, is_approved: true } : u));
    try {
      await api.approveUser(userId)
      toast.success('Пользователь одобрен')
      window.dispatchEvent(new CustomEvent('updateNotificationCounts'))
    } catch (err) {
      setUsers(previousUsers);
      console.error('Ошибка одобрения:', err)
      toast.error('Не удалось одобрить пользователя')
    }
  }

  const handleRejectUser = async (userId) => {
    try {
      await api.deleteAuthUser(userId)
      setUsers(users.filter(u => u.id !== userId))
      toast.success('Заявка отклонена, пользователь удален')
      window.dispatchEvent(new CustomEvent('updateNotificationCounts'))
    } catch (err) {
      console.error('Ошибка отклонения:', err)
      await api.updateProfile(userId, { is_approved: false, login: null, full_name: 'REJECTED_' + userId })
      setUsers(users.map(u => u.id === userId ? { ...u, full_name: 'REJECTED_' + userId, login: null, is_approved: false } : u))
      toast.info('Пользователь скрыт (SQL функция удаления пока недоступна)')
      window.dispatchEvent(new CustomEvent('updateNotificationCounts'))
    }
  }

  const handleChangeRole = async (userId, newRole) => {
    const previousUsers = [...users]
    // Optimistic update
    setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u))
    if (userId === profile.id) {
      setProfile({ ...profile, role: newRole })
    }
    try {
      await api.updateUserRole(userId, newRole)
      toast.success('Роль пользователя обновлена')
    } catch (err) {
      console.error('Ошибка смены роли:', err)
      // Revert on error
      setUsers(previousUsers)
      if (userId === profile.id) {
        setProfile({ ...profile, role: previousUsers.find(u => u.id === profile.id)?.role || profile.role })
      }
      toast.error('Не удалось обновить роль')
    }
  }

  const handleResetPin = (targetUser) => {
    setPinResetModal(targetUser)
  }

  const confirmResetPin = async () => {
    if (!pinResetModal) return
    try {
      await api.updateProfile(pinResetModal.id, { pin_hash: null, pin_attempts: 0, pin_locked_until: null })
      setUsers(prev => prev.map(u => u.id === pinResetModal.id ? { ...u, pin_hash: null } : u))
      
      await api.createSystemNotification(
        pinResetModal.id, 
        'Сброс PIN-кода', 
        'Владелец сбросил ваш PIN-код. Пожалуйста, создайте новый при следующем входе.'
      )
      
      toast.success(`PIN сброшен для ${pinResetModal.full_name}`)
    } catch (err) {
      toast.error('Ошибка сброса PIN: ' + err.message)
    } finally {
      setPinResetModal(null)
    }
  }

  const handleDismissUser = async (userId, preserveData) => {
    if (userId === profile.id) return
    try {
      const targetUser = users.find(u => u.id === userId)
      if (!targetUser) return

      if (preserveData) {
        await api.updateProfile(userId, { is_approved: false, login: null, is_legacy: true })
        setUsers(users.map(u => u.id === userId ? { ...u, is_approved: false, login: null, is_legacy: true } : u))
        toast.success('Сотрудник уволен (данные сохранены)')
      } else {
        await api.deleteAuthUser(userId)
        setUsers(users.filter(u => u.id !== userId))
        toast.success('Сотрудник и все его данные удалены')
      }
    } catch (err) {
      console.error('Ошибка увольнения:', err)
      toast.error('Ошибка при увольнении: ' + err.message)
    } finally {
      setDismissModal(null)
    }
  }

  const handleCreateEmployee = async (e) => {
    e.preventDefault()
    if (!newEmployee.fullName.trim()) return
    setEmployeeSaving(true)
    try {
      await api.createPlaceholderEmployee({
        fullName: newEmployee.fullName.trim(),
        role: newEmployee.role,
        orgId: profile.org_id,
        pvzId: profile.pvz_id,
        hiredAt: newEmployee.hiredAt || new Date().toISOString().split('T')[0],
      })
      toast.success(`Сотрудник «${newEmployee.fullName}» добавлен!`)
      setNewEmployee({ fullName: '', role: 'employee', hiredAt: new Date().toISOString().split('T')[0] })
      setIsAddingEmployee(false)
      loadTeam(true)
    } catch (err) {
      console.error('Ошибка создания сотрудника:', err)
      toast.error('Ошибка: ' + err.message)
    } finally {
      setEmployeeSaving(false)
    }
  }

  const pendingUsers = users.filter(u => !u.is_approved && !u.full_name?.startsWith('REJECTED_') && !u.full_name?.startsWith('BLOCKED_') && !u.is_legacy)
  const approvedUsers = users.filter(u => u.is_approved && !u.full_name?.startsWith('REJECTED_') && !u.is_legacy)
  const archiveUsers = users.filter(u => u.is_legacy)

  const handleSetPassword = async (user) => {
    const newPwd = window.prompt(`Задать новый пароль для ${user.full_name}:\n(Минимум 6 символов)`)
    if (!newPwd) return
    if (newPwd.length < 6) {
      toast.error('Пароль должен быть не менее 6 символов')
      return
    }
    try {
      await api.setEmployeePassword(user.id, newPwd)
      toast.success('Пароль успешно задан!')
    } catch (err) {
      toast.error('Ошибка: ' + err.message)
    }
  }

  const getRoleBadge = (role) => {
    switch (role) {
      case 'owner': return { text: 'Владелец', class: 'role-badge-owner' }
      case 'admin': return { text: 'Админ', class: 'role-badge-admin' }
      case 'admin_employee': return { text: 'Админ-сотрудник', class: 'role-badge-admin-employee' }
      case 'helper': return { text: 'Помощник', class: 'role-badge-helper' }
      default: return { text: 'Сотрудник', class: 'role-badge-employee' }
    }
  }

  return (
    <div className="settings-section animate-fade-in">
      {loadingTeam ? (
        <SkeletonRow count={4} />
      ) : (
        <>
          {pendingUsers.length > 0 && (
            <div className="settings-pending">
              <h3 className="settings-section-title">
                Ожидают одобрения
                <span className="badge badge-danger" style={{ marginLeft: 8 }}>
                  {pendingUsers.length}
                </span>
              </h3>
              <div className="settings-users-list">
                {pendingUsers.map((pUser) => {
                  const targetPvz = userPvzs?.find(pvz => pvz.id === pUser.pvz_id)
                  const pvzName = targetPvz ? targetPvz.name : 'Неизвестный ПВЗ'
                  const formattedDate = new Date(pUser.created_at).toLocaleString('ru-RU', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                  return (
                    <div key={pUser.id} className="settings-user-card">
                      <div className="settings-user-info">
                        <div className="avatar avatar-sm">
                          {pUser.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </div>
                        <div>
                          <div className="settings-user-name">{pUser.full_name}</div>
                          <div className="settings-user-email">
                            {pUser.email} {pUser.login && `· Логин: ${pUser.login}`}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                            На ПВЗ: <strong style={{ color: 'var(--wb-primary)' }}>{pvzName}</strong> · Отправлена: {formattedDate}
                          </div>
                        </div>
                      </div>
                      <div className="settings-user-actions">
                        <button className="btn btn-primary btn-sm" onClick={() => handleApproveUser(pUser.id)}>
                          Одобрить
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleRejectUser(pUser.id)}>
                          Отклонить
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 className="settings-section-title" style={{ margin: 0 }}>Команда ({approvedUsers.length})</h3>
            {!isAddingEmployee && (
              <button className="btn btn-outline btn-sm" onClick={() => setIsAddingEmployee(true)}>
                + Добавить сотрудника
              </button>
            )}
          </div>

          {isAddingEmployee && (
            <form className="animate-fade-in" onSubmit={handleCreateEmployee} style={{ background: 'var(--surface-hover)', padding: '16px', borderRadius: 'var(--border-radius-md)', marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px', padding: '8px', background: 'rgba(255, 193, 7, 0.1)', borderRadius: '8px', border: '1px solid rgba(255, 193, 7, 0.2)' }}>
                Создание профиля без регистрации. Сотрудник сразу появится в графике и расчёте ЗП. Позже он может зарегистрироваться самостоятельно.
              </div>
              <div className="input-group">
                <label>ФИО сотрудника</label>
                <input className="input" required value={newEmployee.fullName} onChange={e => setNewEmployee({...newEmployee, fullName: e.target.value})} placeholder="Иванов Иван Иванович" />
              </div>
              <div className="input-group">
                <CustomSelect
                  label="Роль"
                  value={newEmployee.role}
                  onChange={val => setNewEmployee({...newEmployee, role: val})}
                  options={[
                    { value: 'employee', label: 'Сотрудник' },
                    { value: 'admin', label: 'Админ' },
                    { value: 'helper', label: 'Помощник' },
                  ]}
                />
              </div>
              <div className="input-group">
                <DatePickerField
                  label="Дата устройства"
                  value={newEmployee.hiredAt}
                  onChange={(val) => setNewEmployee({...newEmployee, hiredAt: val})}
                  placeholder="Выберите дату"
                />
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setIsAddingEmployee(false)}>Отмена</button>
                <button type="submit" className="btn btn-primary" disabled={employeeSaving}>
                  {employeeSaving ? 'Сохранение...' : 'Создать сотрудника'}
                </button>
              </div>
            </form>
          )}
          <div className="settings-users-list">
            {approvedUsers.map((aUser) => {
              const role = getRoleBadge(aUser.role)
              return (
                <div key={aUser.id} className="settings-user-card">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <div className="settings-user-info">
                      <div className="avatar avatar-sm">
                        {aUser.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div className="settings-user-name" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          {aUser.full_name}
                          {aUser.is_tutorial_completed ? (
                            <span style={{
                              fontSize: '10px',
                              fontWeight: 600,
                              padding: '2px 8px',
                              borderRadius: '6px',
                              background: 'rgba(16, 185, 129, 0.1)',
                              color: '#10b981',
                              border: '1px solid rgba(16, 185, 129, 0.2)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '2px'
                            }} title="Сотрудник завершил или пропустил вводное обучение">
                              ✓ Обучен
                            </span>
                          ) : (
                            <span style={{
                              fontSize: '10px',
                              fontWeight: 600,
                              padding: '2px 8px',
                              borderRadius: '6px',
                              background: 'rgba(245, 158, 11, 0.1)',
                              color: '#f59e0b',
                              border: '1px solid rgba(245, 158, 11, 0.2)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '2px'
                            }} title="Новый сотрудник, обучение еще не пройдено">
                              Новый
                            </span>
                          )}
                        </div>
                        {(aUser.email || aUser.login || aUser.phone) && (
                          <div className="settings-user-email" style={{ display: 'flex', gap: '8px' }}>
                            {aUser.login && <span>Логин: {aUser.login}</span>}
                            {aUser.email && <span>{aUser.email}</span>}
                            {aUser.phone && <span>{aUser.phone}</span>}
                          </div>
                        )}
                        {/* Привязанные ПВЗ */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                          {aUser.pvzs && aUser.pvzs.length > 0 ? (
                            aUser.pvzs.map((pvz, index) => (
                              <span 
                                key={index} 
                                style={{ 
                                  fontSize: '10px', 
                                  fontWeight: 500, 
                                  padding: '2px 8px', 
                                  borderRadius: '6px', 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  background: pvz.is_primary 
                                    ? 'rgba(203, 17, 171, 0.08)' 
                                    : 'var(--surface-hover)', 
                                  color: pvz.is_primary 
                                    ? 'var(--wb-primary)' 
                                    : 'var(--text-secondary)',
                                  border: pvz.is_primary 
                                    ? '1px solid rgba(203, 17, 171, 0.2)' 
                                    : '1px solid var(--surface-border)',
                                }}
                              >
                                {pvz.is_primary && (
                                  <span style={{ 
                                    width: '4px', 
                                    height: '4px', 
                                    borderRadius: '50%', 
                                    background: 'var(--wb-primary)',
                                    display: 'inline-block' 
                                  }} />
                                )}
                                {pvz.name || `ПВЗ #${pvz.pvz_id}`}
                                {pvz.is_primary && <span style={{ opacity: 0.7, fontSize: '9px', fontWeight: 600 }}>(осн.)</span>}
                              </span>
                            ))
                          ) : (
                            <span style={{ fontSize: '10px', color: 'var(--danger)', background: 'rgba(239, 68, 68, 0.05)', padding: '2px 8px', borderRadius: '6px', border: '1px solid rgba(239, 68, 68, 0.1)' }}>
                              Нет доступа к ПВЗ
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <span className={`settings-role-badge ${role.class}`}>
                      {role.text}
                    </span>
                    {aUser.employment_type === 'substitute' && (
                      <span className="badge" style={{ background: 'rgba(245, 158, 11, 0.12)', color: '#d97706', border: '1px solid rgba(245, 158, 11, 0.25)', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, marginLeft: 6 }}>
                        Подменный
                      </span>
                    )}
                  </div>

                  {isOwner && (
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', fontSize: 12 }}>
                      {aUser.role === 'employee' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ color: 'var(--text-tertiary)', minWidth: 60 }}>Занятость:</span>
                          <div style={{ display: 'inline-flex', background: 'var(--surface-color)', borderRadius: '8px', border: '1px solid var(--border-color)', padding: '2px', gap: '2px' }}>
                            <button
                              type="button"
                              onClick={async () => {
                                if (aUser.employment_type === 'regular' || !aUser.employment_type) return;
                                const previousUsers = [...users];
                                setUsers(prev => prev.map(u => u.id === aUser.id ? { ...u, employment_type: 'regular', payout_frequency_override: null } : u));
                                try {
                                  await api.updateProfile(aUser.id, { employment_type: 'regular', payout_frequency_override: null });
                                  toast.success('Назначен основным сотрудником');
                                } catch (err) {
                                  setUsers(previousUsers);
                                  toast.error('Ошибка: ' + err.message);
                                }
                              }}
                              style={{
                                border: 'none',
                                borderRadius: '6px',
                                padding: '4px 8px',
                                fontSize: '11px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                background: (aUser.employment_type !== 'substitute') ? 'var(--wb-primary)' : 'transparent',
                                color: (aUser.employment_type !== 'substitute') ? '#fff' : 'var(--text-secondary)',
                                transition: 'all 0.15s ease'
                              }}
                            >
                              Основной
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                if (aUser.employment_type === 'substitute') return;
                                const previousUsers = [...users];
                                setUsers(prev => prev.map(u => u.id === aUser.id ? { ...u, employment_type: 'substitute', payout_frequency_override: 'weekly' } : u));
                                try {
                                  await api.updateProfile(aUser.id, { employment_type: 'substitute', payout_frequency_override: 'weekly' });
                                  toast.success('Назначен подменным сотрудником (еженедельно)');
                                } catch (err) {
                                  setUsers(previousUsers);
                                  toast.error('Ошибка: ' + err.message);
                                }
                              }}
                              style={{
                                border: 'none',
                                borderRadius: '6px',
                                padding: '4px 8px',
                                fontSize: '11px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                background: (aUser.employment_type === 'substitute') ? 'var(--wb-primary)' : 'transparent',
                                color: (aUser.employment_type === 'substitute') ? '#fff' : 'var(--text-secondary)',
                                transition: 'all 0.15s ease'
                              }}
                            >
                              Подменный
                            </button>
                          </div>
                          {aUser.employment_type === 'substitute' && (
                            <span style={{ fontSize: 10, color: '#f59e0b', background: 'rgba(245, 158, 11, 0.1)', padding: '2px 6px', borderRadius: 6, fontWeight: 600 }}>
                              Еженедельно
                            </span>
                          )}
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ color: 'var(--text-tertiary)', minWidth: 60 }}>Устроен с:</span>
                        <div style={{ width: '160px' }}>
                          <DatePickerField
                            value={aUser.hired_at ? String(aUser.hired_at).split('T')[0] : ''}
                            onChange={async (val) => {
                              const dateVal = val || null;
                              const previousUsers = [...users];
                              setUsers(prev => prev.map(u => u.id === aUser.id ? { ...u, hired_at: dateVal } : u));
                              try {
                                await api.updateProfile(aUser.id, { hired_at: dateVal })
                                toast.success('Дата устройства обновлена!')
                              } catch (err) {
                                setUsers(previousUsers);
                                toast.error('Ошибка сохранения даты: ' + err.message)
                              }
                            }}
                            placeholder="Не указана"
                          />
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>Периодичность выплат:</span>
                        <div style={{ width: 180 }}>
                          <CustomSelect
                            value={aUser.payout_frequency_override || ''}
                            onChange={async (val) => {
                              const overrideVal = val === '' ? null : val;
                              const previousUsers = [...users];
                              setUsers(prev => prev.map(u => u.id === aUser.id ? { ...u, payout_frequency_override: overrideVal } : u));
                              try {
                                await api.updateProfile(aUser.id, { payout_frequency_override: overrideVal });
                                toast.success(overrideVal ? 'Периодичность выплат обновлена!' : 'Периодичность по настройке ПВЗ');
                              } catch (err) {
                                setUsers(previousUsers);
                                toast.error('Ошибка: ' + err.message);
                              }
                            }}
                            options={[
                              { value: '', label: 'По настройке ПВЗ' },
                              { value: 'weekly', label: 'Еженедельно' },
                              { value: 'bi_monthly', label: '2 раза в месяц' },
                              { value: 'monthly', label: 'Раз в месяц' },
                              { value: 'shifts', label: 'По сменам' },
                            ]}
                          />
                        </div>
                        {aUser.payout_frequency_override && (
                          <span style={{ fontSize: 10, color: 'var(--wb-primary)', background: 'rgba(203,17,171,0.08)', padding: '2px 6px', borderRadius: 6, fontWeight: 600 }}>Индивид. график</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ color: 'var(--text-tertiary)', minWidth: 60 }}>День выплаты:</span>
                        <div style={{ width: 180 }}>
                          <CustomSelect
                            value={aUser.pay_day != null ? String(aUser.pay_day) : ''}
                            onChange={async (val) => {
                              const parsed = val === '' ? null : parseInt(val, 10);
                              const previousUsers = [...users];
                              setUsers(prev => prev.map(u => u.id === aUser.id ? { ...u, pay_day: parsed } : u));
                              try {
                                  await api.updateProfile(aUser.id, { pay_day: parsed })
                                  toast.success(parsed !== null ? `День выплаты обновлён!` : 'Выплата по стандарту')
                              } catch (err) {
                                setUsers(previousUsers);
                                toast.error('Ошибка: ' + err.message)
                              }
                            }}
                            options={[
                              { value: '', label: 'По стандарту' },
                              { value: '1', label: 'Понедельник' },
                              { value: '2', label: 'Вторник' },
                              { value: '3', label: 'Среда' },
                              { value: '4', label: 'Четверг' },
                              { value: '5', label: 'Пятница' },
                              { value: '6', label: 'Суббота' },
                              { value: '0', label: 'Воскресенье' },
                            ]}
                          />
                        </div>
                        {aUser.pay_day != null && (
                          <span style={{ fontSize: 10, color: 'var(--wb-primary)', background: 'rgba(203,17,171,0.08)', padding: '2px 6px', borderRadius: 6, fontWeight: 600 }}>Индивид. день</span>
                        )}
                      </div>
                    </div>
                  )}

                  {isOwner && aUser.id !== profile.id && (
                    <div className="settings-user-actions">
                      <div style={{ width: 160 }}>
                        <CustomSelect
                          value={aUser.role}
                          onChange={(val) => handleChangeRole(aUser.id, val)}
                          options={[
                            { value: 'employee', label: 'Сотрудник' },
                            { value: 'admin', label: 'Админ' },
                            { value: 'helper', label: 'Помощник' },
                          ]}
                        />
                      </div>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--wb-primary)', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', whiteSpace: 'nowrap' }}
                        onClick={() => setMergeModal({ placeholderId: aUser.id, fullName: aUser.full_name })}
                        title="Объединить с новым аккаунтом"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
                        Объединить
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--wb-primary)', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', whiteSpace: 'nowrap' }}
                        onClick={() => handleOpenPvzModal(aUser)}
                        title="Настроить доступ к ПВЗ"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        Доступ к ПВЗ
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--wb-primary)', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', whiteSpace: 'nowrap' }}
                        onClick={() => handleSetPassword(aUser)}
                        title="Задать пароль для входа"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        Задать пароль
                      </button>
                      {aUser.pin_hash && (
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ color: 'var(--warning)', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', whiteSpace: 'nowrap' }}
                          onClick={() => handleResetPin(aUser)}
                          title="Сбросить PIN-код"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>
                          Сбросить PIN
                        </button>
                      )}
                      <button
                        className="btn btn-ghost btn-sm text-danger"
                        style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', whiteSpace: 'nowrap', marginLeft: 'auto' }}
                        onClick={() => setDismissModal(aUser)}
                        title="Уволить сотрудника"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="18" y1="8" x2="23" y2="13"/><line x1="23" y1="8" x2="18" y2="13"/></svg>
                        Уволить
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {archiveUsers.length > 0 && (
            <div style={{ marginTop: '32px' }}>
              <button 
                className="btn btn-ghost btn-sm" 
                style={{ width: '100%', justifyContent: 'space-between', padding: '12px', border: '1px dashed var(--border-color)', borderRadius: '12px' }}
                onClick={() => setShowArchive(!showArchive)}
              >
                <span>Архивные профили ({archiveUsers.length})</span>
                <span>{showArchive ? 'Свернуть' : 'Развернуть'}</span>
              </button>
              
              {showArchive && (
                <div className="settings-users-list" style={{ marginTop: '12px' }}>
                  {archiveUsers.map((arcUser) => {
                    const role = getRoleBadge(arcUser.role)
                    return (
                      <div key={arcUser.id} className="settings-user-card" style={{ opacity: 0.7 }}>
                        <div className="settings-user-info">
                          <div className="avatar avatar-sm" style={{ background: 'var(--text-secondary)' }}>
                            {arcUser.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </div>
                          <div>
                            <div className="settings-user-name">{arcUser.full_name}</div>
                            <div className="settings-user-email">Архивный профиль</div>
                          </div>
                          <span className={`settings-role-badge ${role.class}`}>
                            {role.text}
                          </span>
                        </div>
                        {isOwner && (
                          <div className="settings-user-actions">
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => setConfirmRestore(arcUser)}
                            >
                              Восстановить
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Модалка объединения аккаунтов */}
      <Modal 
        isOpen={!!mergeModal} 
        onClose={() => setMergeModal(null)}
        title="Привязать аккаунт"
      >
        <div style={{ marginBottom: 16 }}>
          Выберите зарегистрированного сотрудника (на модерации), которого нужно объединить с профилем <strong>{mergeModal?.fullName}</strong>:
          <br /><br />
          <small style={{ color: 'var(--text-secondary)' }}>
            Все смены, операции и штрафы будут перенесены на новый аккаунт. Старый профиль-заглушка будет удален.
          </small>
        </div>
        
        {pendingUsers.length === 0 ? (
          <div className="empty-state sm">
            Нет сотрудников, ожидающих одобрения.<br />Пусть сотрудник сначала зарегистрируется по вашей инвайт-ссылке ПВЗ.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pendingUsers.map(u => (
              <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 12, border: '1px solid var(--border-color)', borderRadius: 8 }}>
                <div>
                  <strong>{u.full_name}</strong>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{u.email}</div>
                </div>
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={async () => {
                    try {
                      await api.mergeEmployeeProfiles(mergeModal.placeholderId, u.id)
                      setMergeModal(null)
                      loadTeam(true)
                    } catch (err) {
                      console.error(err)
                      toast.error('Ошибка: ' + err.message)
                    }
                  }}
                >
                  Выбрать
                </button>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* PIN Reset Modal */}
      <Modal isOpen={!!pinResetModal} title="Сброс PIN-кода" onClose={() => setPinResetModal(null)}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: '280px' }}>
          <div style={{ textAlign: 'center', padding: '16px 0 8px' }}>
            <p style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>
              Сбросить PIN для <strong>{pinResetModal?.full_name}</strong>?
            </p>
          </div>
          <div style={{ padding: '12px 16px', background: 'var(--surface-hover)', borderRadius: '12px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            При следующем входе сотруднику будет предложено создать новый PIN-код. Текущие данные будут удалены.
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setPinResetModal(null)}>
              Отмена
            </button>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={confirmResetPin}>
              Сбросить PIN
            </button>
          </div>
        </div>
      </Modal>

      {/* Dismiss Employee Modal */}
      {dismissModal && (
        <Modal 
          isOpen={true} 
          title="Увольнение сотрудника" 
          onClose={() => setDismissModal(null)}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: '300px' }}>
            <p style={{ margin: 0 }}>
              Вы собираетесь уволить сотрудника <b>{dismissModal.full_name}</b>.
            </p>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
              Выберите, что сделать с историей его операций, смен и зарплат:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '12px', background: 'var(--surface-hover)', borderRadius: '8px' }}>
                <button 
                  className="btn btn-primary" 
                  style={{ width: '100%' }}
                  onClick={() => handleDismissUser(dismissModal.id, true)}
                >
                  Сохранить исторические данные
                </button>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.3 }}>
                  Сотрудник перейдёт в архив, история выплат и статистика останутся
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '12px', background: 'rgba(239, 68, 68, 0.05)', borderRadius: '8px', border: '1px solid rgba(239, 68, 68, 0.1)' }}>
                <button 
                  className="btn btn-danger" 
                  style={{ width: '100%' }}
                  onClick={() => {
                    setConfirmHardDelete(dismissModal)
                    setDismissModal(null)
                  }}
                >
                  Удалить профиль полностью
                </button>
                <div style={{ fontSize: '11px', color: 'var(--danger)', textAlign: 'center', opacity: 0.8, lineHeight: 1.3 }}>
                  Безвозвратно удалит все его смены, операции и расчёты
                </div>
              </div>
            </div>
            <button className="btn btn-ghost" style={{ marginTop: '8px' }} onClick={() => setDismissModal(null)}>
              Отмена
            </button>
          </div>
        </Modal>
      )}

      {/* Модалка второго подтверждения безвозвратного удаления */}
      <Modal isOpen={!!confirmHardDelete} onClose={() => setConfirmHardDelete(null)} title="Безвозвратное удаление">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '320px', padding: '8px 0 16px' }}>
          <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.5 }}>
            Вы собираетесь безвозвратно удалить профиль сотрудника <strong>{confirmHardDelete?.full_name}</strong>.
          </p>
          <div style={{ padding: '10px 12px', background: 'rgba(239, 68, 68, 0.08)', borderRadius: '8px', borderLeft: '3px solid var(--danger)', fontSize: '13px', color: 'var(--danger)', fontWeight: 500 }}>
            Это действие нельзя отменить. Все его персональные смены, операции, начисления и доступы будут удалены.
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setConfirmHardDelete(null)}>
            Отмена
          </button>
          <button
            className="btn btn-danger"
            style={{ flex: 1 }}
            onClick={async () => {
              const targetId = confirmHardDelete.id
              setConfirmHardDelete(null)
              await handleDismissUser(targetId, false)
            }}
          >
            Удалить навсегда
          </button>
        </div>
      </Modal>

      {/* Модалка подтверждения восстановления */}
      <Modal isOpen={!!confirmRestore} onClose={() => setConfirmRestore(null)} title="Восстановление сотрудника">
        <div style={{ padding: '10px 0 20px', color: 'var(--text-secondary)' }}>
          Восстановить сотрудника <strong>{confirmRestore?.full_name}</strong> из архива?
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setConfirmRestore(null)}>Отмена</button>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={async () => {
            try {
              await api.updateProfile(confirmRestore.id, { is_legacy: false })
              setUsers(users.map(u => u.id === confirmRestore.id ? { ...u, is_legacy: false } : u))
              toast.success('Профиль восстановлен')
            } catch (err) {
              toast.error('Ошибка: ' + err.message)
            } finally {
              setConfirmRestore(null)
            }
          }}>Восстановить</button>
        </div>
      </Modal>

      {/* Модалка настройки доступов к ПВЗ */}
      <Modal 
        isOpen={!!pvzModalUser} 
        onClose={() => setPvzModalUser(null)}
        title={`Доступ к ПВЗ: ${pvzModalUser?.full_name}`}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: '320px', padding: '8px 0' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
            Отметьте ПВЗ, на которые сотрудник имеет право выходить.
            Также выберите один <strong>основной ПВЗ</strong> (для базовых настроек и домашнего отображения).
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '300px', overflowY: 'auto', paddingRight: '4px' }}>
            {pvzList.map((pvz) => (
              <div 
                key={pvz.id} 
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  padding: '12px 16px', 
                  background: 'var(--surface-hover)', 
                  border: pvz.is_primary ? '1px solid var(--wb-primary)' : '1px solid var(--surface-border)',
                  borderRadius: '12px',
                  transition: 'all 0.2s ease'
                }}
              >
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: 0, flex: 1, fontWeight: 500 }}>
                  <input 
                    type="checkbox" 
                    checked={!!pvz.assigned}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setPvzList(prev => prev.map(p => {
                        if (p.id === pvz.id) {
                          const isPrimary = checked ? p.is_primary : false;
                          return { ...p, assigned: checked, is_primary: isPrimary };
                        }
                        return p;
                      }));
                    }}
                    style={{ 
                      accentColor: 'var(--wb-primary)', 
                      width: '18px', 
                      height: '18px',
                      cursor: 'pointer'
                    }}
                  />
                  <span>{pvz.name || `ПВЗ #${pvz.id}`}</span>
                </label>

                {pvz.assigned && (
                  <button
                    type="button"
                    className={`btn btn-sm ${pvz.is_primary ? 'btn-primary' : 'btn-outline'}`}
                    style={{ 
                      padding: '4px 10px', 
                      fontSize: '11px', 
                      height: '26px',
                      borderRadius: '8px'
                    }}
                    onClick={() => {
                      setPvzList(prev => prev.map(p => ({
                        ...p,
                        is_primary: p.id === pvz.id
                      })));
                    }}
                  >
                    {pvz.is_primary ? 'Основной' : 'Сделать основным'}
                  </button>
                )}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button 
              className="btn btn-ghost" 
              style={{ flex: 1 }} 
              onClick={() => setPvzModalUser(null)}
              disabled={savingPvz}
            >
              Отмена
            </button>
            <button 
              className="btn btn-primary" 
              style={{ flex: 1 }} 
              onClick={handleSavePvzs}
              disabled={savingPvz}
            >
              {savingPvz ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
